import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiMessageSquare, FiSend, FiAlertCircle, FiTrash2 } from 'react-icons/fi';
import { chatInfra, type ChatMessage } from '../../api/ai';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  nodes: any[];
  edges: any[];
  provider: string;
  environment: string;
  architectureName?: string;
  projectName?: string;
}

const SUGGESTED_QUESTIONS = [
  'Quelles sont les failles de sécurité de cette architecture ?',
  'Comment améliorer la résilience ?',
  'Quelles ressources sont surdimensionnées ?',
  'Comment monitorer cette infrastructure ?',
  'Quels sont les risques en cas de panne de la zone principale ?',
];

function MessageBubble({ msg }: { msg: ChatMessage }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
        isUser
          ? 'bg-purple-600 text-white rounded-br-sm'
          : 'bg-gray-100 text-gray-800 rounded-bl-sm'
      }`}>
        {msg.content.split('\n').map((line, i) => (
          <React.Fragment key={i}>
            {line}
            {i < msg.content.split('\n').length - 1 && <br />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

const ChatModal: React.FC<Props> = ({ isOpen, onClose, nodes, edges, provider, environment, architectureName, projectName }) => {
  const [messages, setMessages]   = useState<ChatMessage[]>([]);
  const [input, setInput]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const bottomRef                 = useRef<HTMLDivElement>(null);
  const inputRef                  = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setMessages([]);
      setInput('');
      setError(null);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = async (text?: string) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');
    setError(null);

    const userMsg: ChatMessage = { role: 'user', content: msg };
    const optimisticHistory = [...messages, userMsg];
    setMessages(optimisticHistory);
    setLoading(true);

    try {
      const result = await chatInfra({
        message: msg,
        conversationHistory: messages,
        nodes, edges, provider, environment, architectureName, projectName,
      });
      setMessages(result.conversationHistory);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erreur');
      setMessages(messages); // rollback
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl h-[80vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center">
              <FiMessageSquare size={18} className="text-purple-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Chat Infrastructure</h2>
              {architectureName && <p className="text-xs text-gray-500">{architectureName} · {provider}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <button onClick={() => setMessages([])} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
                <FiTrash2 size={12} /> Effacer
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400"><FiX size={18} /></button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {messages.length === 0 && (
            <div className="space-y-4">
              <div className="text-center py-6">
                <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto mb-3">
                  <FiMessageSquare size={24} className="text-purple-500" />
                </div>
                <p className="text-sm text-gray-600 mb-1">Posez n'importe quelle question sur votre infrastructure</p>
                <p className="text-xs text-gray-400">Claude connaît le contexte de votre architecture</p>
              </div>
              <div className="space-y-2">
                {SUGGESTED_QUESTIONS.map((q, i) => (
                  <button key={i} onClick={() => send(q)}
                    className="w-full text-left text-sm px-4 py-2.5 rounded-xl border border-gray-100 hover:border-purple-200 hover:bg-purple-50 text-gray-700 transition-colors">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => <MessageBubble key={i} msg={m} />)}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-2">
                <div className="flex gap-1">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600">
              <FiAlertCircle size={14} className="shrink-0" />
              {error}
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="px-6 py-4 border-t border-gray-100 shrink-0">
          <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-2 border border-gray-200 focus-within:border-purple-400 focus-within:ring-2 focus-within:ring-purple-100 transition-all">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Posez une question sur votre infrastructure…"
              disabled={loading}
              className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none"
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || loading}
              className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:bg-purple-700 transition-colors"
            >
              <FiSend size={14} className="text-white" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatModal;
